/**
 * Goal tracking chart — shows a metric trend over time with a horizontal
 * goal line, scoped to the goal's portfolio group. Optionally overlays
 * benchmark comparison lines from other scopes.
 *
 * Uses the performance API with the goal's measure/metric and portfolio_group.
 */
import { useState, useEffect, useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { api } from '../../services/api'
import type { Goal, MeasureDefinition } from '../../types/exec'
import { formatMetric, type MetricKey } from './generic/types'
import type { BenchmarkDataSeries } from './generic/useBenchmarkQueries'

interface Props {
  goal: Goal
  measureDef: MeasureDefinition | undefined
  benchmarkSeries?: BenchmarkDataSeries[]
}

/**
 * Resolve a goal's kpi_field to the API endpoint and read-field needed to
 * compute its trend. Most kpi_fields live on /api/govdata/performance/, but
 * percentile-based speed and duration fields live on dedicated marts:
 * /api/govdata/speed-to-contract/ and /api/govdata/contract-duration/.
 *
 * Issue #90 §5 + Phase B5 fix: previously the speed/duration cases silently
 * fell back to `award_count` from the performance endpoint, so goal trend
 * lines for those measures showed total award volume instead of percentile
 * days. That deferred fix lands here.
 */
const SPEED_KPI_FIELDS = new Set([
  'p25_solicitation_to_award', 'p50_solicitation_to_award',
  'p75_solicitation_to_award', 'p90_solicitation_to_award',
])
const DURATION_KPI_FIELDS = new Set([
  'p25_duration_days', 'p50_duration_days', 'p75_duration_days', 'p90_duration_days',
  'avg_duration_days', 'min_duration_days', 'max_duration_days',
])
const PERFORMANCE_FALLBACK_MAP: Record<string, MetricKey> = {
  full_open_pct: 'full_open_pct',
  sb_dollar_pct: 'sb_dollar_pct',
  set_aside_pct: 'set_aside_pct',
  sb_award_pct: 'sb_award_pct',
  q4_share_of_fy: 'award_count', // not on performance_fact; q4 is on fy-end-surge mart (TBD)
  total_awards: 'award_count',
  award_count: 'award_count',
  ot_award_pct: 'ot_award_pct', // rate column now on performance_fact (M1)
  ot_dollar_pct: 'ot_dollar_pct', // OT obligated-dollar share (from parcat branch)
}

interface ResolvedEndpoint {
  url: string
  /** Field name to read from each row in the API response */
  field: string
  /** Whether this field is a days-valued percentile (formats as "Xd") */
  isDays: boolean
}

function resolveEndpoint(kpiField: string | null | undefined): ResolvedEndpoint {
  if (!kpiField) {
    return { url: '/api/govdata/performance/', field: 'award_count', isDays: false }
  }
  if (SPEED_KPI_FIELDS.has(kpiField)) {
    return { url: '/api/govdata/speed-to-contract/', field: kpiField, isDays: true }
  }
  if (DURATION_KPI_FIELDS.has(kpiField)) {
    return { url: '/api/govdata/contract-duration/', field: kpiField, isDays: true }
  }
  const mapped = PERFORMANCE_FALLBACK_MAP[kpiField] || 'award_count'
  return { url: '/api/govdata/performance/', field: mapped, isDays: false }
}

/** Format a value for display, days-aware. */
function formatGoalValue(field: string, value: number | null | undefined, isDays: boolean): string {
  if (value === null || value === undefined) return '—'
  if (isDays) return `${Math.round(value)}d`
  return formatMetric(field as MetricKey, value)
}

interface PerfRow {
  [key: string]: any
}

export default function GoalTrackingChart({ goal, measureDef, benchmarkSeries }: Props) {
  const [data, setData] = useState<PerfRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const endpoint = useMemo(() => resolveEndpoint(measureDef?.kpi_field), [measureDef?.kpi_field])

  useEffect(() => {
    // The /performance/ endpoint groups by FY when given group_by=fiscal_year.
    // The /speed-to-contract/ and /contract-duration/ endpoints already return
    // one row per (office × FY × …) so we filter+aggregate client-side.
    const params: Record<string, string> = {}
    if (endpoint.url === '/api/govdata/performance/') {
      params.group_by = 'fiscal_year'
    }
    if (goal.portfolio_group) {
      // Phase M.M2 added portfolio_group filter parity to all per-mart
      // endpoints, so this works on /performance/, /speed-to-contract/,
      // /contract-duration/, /vendor-concentration/, and /fy-end-surge/.
      params.portfolio_group = goal.portfolio_group
    }

    api
      .getRaw<{ results: PerfRow[] }>(endpoint.url, params)
      .then((resp) => {
        setData(resp.results)
        setError(null)
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : 'Failed to load goal data'
        setError(msg)
      })
      .finally(() => setLoading(false))
  }, [goal.portfolio_group, endpoint.url])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-red-500" title={error}>
        Failed to load goal data
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-gray-400">
        No data for this portfolio
      </div>
    )
  }

  // Filter out FY2026+ — incomplete data makes charts look misleading
  const filtered = data.filter((r) => Number(r.fiscal_year) < 2026)

  if (filtered.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-gray-400">
        No data for this portfolio
      </div>
    )
  }

  // Build master categories from primary data + all benchmark data
  const primaryFYs = new Set(filtered.map((r) => Number(r.fiscal_year)))
  const bmSeries = benchmarkSeries || []
  for (const bs of bmSeries) {
    for (const row of bs.data) {
      const fy = Number(row.fiscal_year)
      if (fy < 2026) primaryFYs.add(fy)
    }
  }
  const allFYs = Array.from(primaryFYs).sort((a, b) => a - b)
  const categories = allFYs.map((fy) => `FY${fy}`)

  // Build primary value array aligned to master categories.
  // /performance/ returns one row per FY when group_by=fiscal_year.
  // /speed-to-contract/ + /contract-duration/ return many rows per FY (one per
  // office × extent_competed × idv_type / vehicle), so we aggregate via a
  // count-weighted mean using award_count as the weight. Note: weighted mean of
  // percentiles is mathematically approximate; proper aggregation would need
  // server-side support (M.I1 backend ask). Acceptable for goal tracking where
  // the user wants a single trend line, not statistical purity.
  const primaryByFY = new Map<number, number>()
  if (endpoint.url === '/api/govdata/performance/') {
    for (const r of filtered) {
      const val = r[endpoint.field]
      primaryByFY.set(Number(r.fiscal_year), typeof val === 'number' ? val : parseFloat(String(val)) || 0)
    }
  } else {
    const buckets = new Map<number, { sum: number; weight: number }>()
    for (const r of filtered) {
      const fy = Number(r.fiscal_year)
      if (Number.isNaN(fy)) continue
      const v = typeof r[endpoint.field] === 'number'
        ? Number(r[endpoint.field])
        : parseFloat(String(r[endpoint.field]))
      if (Number.isNaN(v)) continue
      const w = Number(r.award_count ?? 1) || 1
      if (!buckets.has(fy)) buckets.set(fy, { sum: 0, weight: 0 })
      const bucket = buckets.get(fy)!
      bucket.sum += v * w
      bucket.weight += w
    }
    for (const [fy, b] of buckets) {
      primaryByFY.set(fy, b.weight > 0 ? b.sum / b.weight : 0)
    }
  }
  const values = allFYs.map((fy) => primaryByFY.get(fy) ?? null)

  const goalValue = goal.target_value.value
  const isLower = goal.target_value.direction === 'lower_is_better'
  const hasBenchmarks = bmSeries.length > 0 && bmSeries.some((bs) => bs.data.length > 0)

  // Build ECharts series
  const series: any[] = [
    // Primary data line
    {
      name: goal.portfolio_group_name || 'Portfolio',
      type: 'line',
      data: values,
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2, color: '#3b82f6' },
      itemStyle: {
        color: (params: any) => {
          const val = params.value
          if (val === null) return '#9ca3af'
          if (isLower) return val <= goalValue ? '#22c55e' : '#ef4444'
          return val >= goalValue ? '#22c55e' : '#ef4444'
        },
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(59, 130, 246, 0.15)' },
            { offset: 1, color: 'rgba(59, 130, 246, 0)' },
          ],
        },
      },
    },
    // Goal target line
    {
      name: 'Goal',
      type: 'line',
      data: allFYs.map(() => goalValue),
      lineStyle: { width: 2, type: 'dashed', color: '#f59e0b' },
      symbol: 'none',
      label: {
        show: true,
        position: 'end' as const,
        formatter: `Goal: ${formatGoalValue(endpoint.field, goalValue, endpoint.isDays)}`,
        fontSize: 10,
        color: '#f59e0b',
      },
    },
  ]

  // Benchmark overlay lines.
  // Benchmarks are loaded by useBenchmarkQueries against /performance/, so for
  // speed/duration goals the benchmark data won't carry the percentile field
  // and the overlay will show empty until useBenchmarkQueries is taught about
  // the dedicated marts (follow-on work).
  for (const bs of bmSeries) {
    if (bs.data.length === 0) continue
    const bmByFY = new Map<number, number>()
    for (const row of bs.data) {
      const fy = Number(row.fiscal_year)
      if (fy >= 2026) continue
      const val = row[endpoint.field]
      bmByFY.set(fy, typeof val === 'number' ? val : parseFloat(String(val)) || 0)
    }
    series.push({
      name: bs.source.label,
      type: 'line',
      data: allFYs.map((fy) => bmByFY.get(fy) ?? null),
      smooth: true,
      symbol: 'diamond',
      symbolSize: 4,
      lineStyle: { width: 1.5, type: 'dashed', color: bs.source.color || '#8b5cf6' },
      itemStyle: { color: bs.source.color || '#8b5cf6' },
    })
  }

  const option = {
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const header = items[0]?.axisValue || ''
        const lines: string[] = []
        for (const p of items) {
          if (p.seriesName === 'Goal') continue
          if (p.value === null || p.value === undefined) continue
          const formatted = formatGoalValue(endpoint.field, p.value, endpoint.isDays)
          // Show on-track status only for the primary series
          if (p.seriesIndex === 0) {
            const statusHtml = isLower
              ? (p.value <= goalValue ? '<span style="color:#22c55e">On Track</span>' : '<span style="color:#ef4444">Above Target</span>')
              : (p.value >= goalValue ? '<span style="color:#22c55e">On Track</span>' : '<span style="color:#ef4444">Below Target</span>')
            lines.push(`${p.marker} ${p.seriesName}: <b>${formatted}</b> ${statusHtml}`)
          } else {
            lines.push(`${p.marker} ${p.seriesName}: <b>${formatted}</b>`)
          }
        }
        return `<b>${header}</b><br/>${lines.join('<br/>')}`
      },
    },
    ...(hasBenchmarks ? { legend: { top: 0, textStyle: { fontSize: 10 }, itemWidth: 16, itemHeight: 8 } } : {}),
    grid: { left: 50, right: 20, top: hasBenchmarks ? 30 : 15, bottom: 30 },
    xAxis: {
      type: 'category' as const,
      data: categories,
      axisLabel: { fontSize: 10 },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 10 },
    },
    series,
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  )
}
